import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { 
  LayoutDashboard, 
  Package, 
  Box, 
  GitMerge, 
  Factory, 
  Truck, 
  FileText,
  Gauge,
  ClipboardList,
  Users,
  LogOut,
  PackagePlus,
  Building2,
  PackageCheck,
  Settings,
  TrendingUp,
  CheckCircle,
  Workflow,
  ShoppingCart,
  ArrowLeftRight,
  Receipt,
  Activity,
  Shield,
  PlusCircle,
  List,
  Palette,
  AlertTriangle,
  Calculator,
  Warehouse,
  Database,
  DatabaseBackup,
  Layers,
  Link2,
  Upload,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import BranchSelector from "@/components/BranchSelector";
import useAuthStore from "@/store/authStore";
import { hasAnyRole } from "@/utils/roleGuard";
import { Button } from "@/components/ui/button";

// Role display names mapping
const ROLE_DISPLAY_NAMES = {
  'master_admin': 'MASTER ADMIN',
  'branch_user': 'BRANCH USER',
  'MASTER_ADMIN': 'MASTER ADMIN',
  'DEMAND_PLANNER': 'DEMAND PLANNER',
  'TECH_OPS_ENGINEER': 'TECH OPS ENGINEER',
  'CPC_PLANNER': 'CPC PLANNER',
  'PROCUREMENT_OFFICER': 'PROCUREMENT',
  'BRANCH_OPS_USER': 'BRANCH OPS',
  'QUALITY_INSPECTOR': 'QUALITY',
  'LOGISTICS_COORDINATOR': 'LOGISTICS',
  'FINANCE_VIEWER': 'FINANCE USER',
  'AUDITOR_READONLY': 'AUDITOR'
};

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, userRoles, isMasterAdmin, hasRole, hasPermission, canRead, canCreate, canUpdate } = useAuthStore();
  const isAdmin = isMasterAdmin();

  // Navigation items — data-driven from the RBAC matrix.
  // Each item declares ONE of:
  //   • entity:  shown if user has `canRead(entity)` — the matrix is the
  //              source of truth, so admins can toggle visibility per role
  //              from the Role Permissions screen without code changes.
  //   • entity + action: gate on a specific action (e.g. RM Repository
  //              requires UPDATE because it's a master-data editor).
  //   • roles:   legacy fallback for pages that aren't yet wired to a
  //              specific RBAC entity (Color Development, Tech Ops master
  //              data editor, RM Production, Quality, Reports).
  //   • adminOnly: visible only to MASTER_ADMIN (used for system tools).
  // CPC_PLANNER should NOT see: SKUs page, branch dropdown.
  const isCPCPlanner = hasRole('CPC_PLANNER') && !isAdmin;

  // Sidebar visibility resolver. Returns true if the menu item should
  // appear for the current user. MASTER_ADMIN bypasses every check.
  const canSee = (item) => {
    if (isAdmin) return true;
    if (item.adminOnly) return false;
    if (item.anyAuth) return true;
    // Role-allowlist items use the shared union helper so the sidebar's
    // visibility decision is byte-identical to the route guard's
    // access decision (see `App.js::RoleRoute` + `utils/roleGuard`).
    if (item.roles) return hasAnyRole({ user, userRoles }, item.roles);
    if (item.entity) {
      const action = item.action || 'READ';
      switch (action) {
        case 'CREATE': return canCreate(item.entity);
        case 'UPDATE': return canUpdate(item.entity);
        case 'READ':
        default: return canRead(item.entity);
      }
    }
    return false;
  };

  // ──────────────────────────────────────────────────────────────────────
  // Sidebar definition (May 2026 — data-driven RBAC)
  // ──────────────────────────────────────────────────────────────────────
  const navGroups = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      items: [
        // Every authenticated user lands on the dashboard.
        { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, anyAuth: true },
        // Master Dashboard is a Tech Ops cross-org view — no clean entity.
        { path: "/master-dashboard", label: "Master Dashboard", icon: Gauge, roles: ['TECH_OPS_ENGINEER'] },
        // Company Pulse (Jul 2026, RBAC-migrated Feb 2026) — leadership
        // exec view. Sidebar visibility now driven by the `Pulse:READ`
        // permission entity (backend `role_permissions`), NOT a
        // hardcoded role list. Grant/revoke `Pulse:READ` in the
        // RoleManagement matrix and the item appears/disappears with
        // no code change or redeploy.
        { path: "/pulse", label: "Company Pulse", icon: Activity, entity: 'Pulse', action: 'READ' },
        // Event System — moved out of Admin (June 2026) so every authenticated
        // user can see the system-wide event log + Inventory Impact Log. Write
        // actions (backfill / manual publish) remain master-admin-gated on the
        // backend so opening visibility is safe.
        { path: "/events", label: "Event System", icon: Activity, anyAuth: true },
      ],
    },
    {
      key: "catalog",
      label: "Catalog",
      icon: Box,
      items: [
        // Tech Ops master-data editor (Vertical/Model/Brand writes) — no single entity.
        { path: "/techops", label: "Tech Ops", icon: Settings, roles: ['TECH_OPS_ENGINEER'] },
        // Mould Master — physical moulds drive INP BOMs. Gated to Tech Ops + Master Admin.
        { path: "/moulds", label: "Moulds", icon: Factory, roles: ['TECH_OPS_ENGINEER', 'MASTER_ADMIN'] },
        // Buyer SKU management & viewing — gated by BuyerSKU READ.
        { path: "/sku-management", label: "SKU Management", icon: Box, entity: 'BuyerSKU' },
        { path: "/demand-sku-view", label: "SKU Catalog", icon: List, entity: 'BuyerSKU' },
        // Color Development (Pantone / Master Batches) — Demand-specific tool, not in RBAC matrix.
        { path: "/color-development", label: "Color Development", icon: Palette, roles: ['DEMAND_PLANNER'] },
        // Colourway Composer — relocated from Demand. Read+visibility limited to
        // catalog-curator roles (Master Admin, Tech Ops, Demand Planner) per the
        // RBAC review (Feb 2026). Backend write/delete gates unchanged.
        { path: "/colourway", label: "Colourway", icon: Palette, roles: ['MASTER_ADMIN', 'TECH_OPS_ENGINEER', 'DEMAND_PLANNER'] },
        { path: "/sku-subscription", label: "SKU Subscription", icon: PackageCheck, entity: 'BuyerSKU' },
      ],
    },
    {
      // Top-level standalone — renders as a single NavLink in the sidebar
      // (no collapsible group wrapper) so floor users get one-click access.
      // The `standalone: true` flag is consumed by the render loop below.
      key: "bom-lookup",
      label: "BOM Lookup",
      icon: Layers,
      standalone: true,
      items: [
        { path: "/bom-lookup", label: "BOM Lookup", icon: Layers, anyAuth: true },
      ],
    },
    {
      key: "demand",
      label: "Demand",
      icon: TrendingUp,
      items: [
        // Demand Hub is a Demand-Planner-only creation workspace.
        { path: "/demand-hub", label: "Demand Hub", icon: PlusCircle, roles: ['DEMAND_PLANNER'] },
        { path: "/demand", label: "Demand Forecasts", icon: TrendingUp, entity: 'Forecast' },
        { path: "/dispatch-lots", label: "Dispatch Lots", icon: Package, entity: 'DispatchLot' },
        { path: "/buyer-management", label: "Buyer Management", icon: Users, entity: 'Buyer' },
      ],
    },
    {
      key: "procurement",
      label: "Procurement",
      icon: ShoppingCart,
      items: [
        { path: "/procurement", label: "Procurement", icon: ShoppingCart, entity: 'Vendor' },
        // Feb 2026 Phase 2 — unified Purchase Orders manager (replaces
        // the standalone MRP Draft POs page; MRP-generated POs surface
        // here tagged source=MRP_DRAFT).
        { path: "/procurement/purchase-orders", label: "Purchase Orders", icon: FileText, entity: 'PurchaseOrder' },
        { path: "/vendors", label: "Vendor Management", icon: Building2, entity: 'Vendor' },
        // RM Repository — the master-data editor. Gated by READ so users
        // with view-only access can browse it; PermButton on the page itself
        // disables every Create/Edit/Delete affordance for users lacking
        // RawMaterial.UPDATE / CREATE / DELETE. The API enforces the write
        // perms regardless, so even a tampered client can't escalate.
        { path: "/rm-repository", label: "RM Repository", icon: Package, entity: 'RawMaterial' },
        { path: "/rm-shortage", label: "RM Shortage", icon: AlertTriangle, entity: 'RMStockMovement' },
        { path: "/rm-required", label: "RM Required", icon: Calculator, entity: 'RMStockMovement' },
        // Phase 6D — Pending-for-Invoice vendor-FG aging report.
        { path: "/pending-for-invoice", label: "Pending for Invoice", icon: AlertTriangle, entity: 'FGInwardBill' },
      ],
    },
    {
      // Feb 2026 (MRP frontend rollout) — MRP module surfaced with a
      // top-level sidebar group. Six items split by input vs output.
      // Roles allowlist matches the router-level guard on /api/mrp/*
      // (MASTER_ADMIN, CPC_PLANNER, PROCUREMENT_OFFICER) — sidebar
      // visibility and route access stay byte-identical.
      key: "mrp",
      label: "MRP",
      icon: Calculator,
      items: [
        { path: "/mrp", label: "MRP Overview", icon: Gauge,
          roles: ['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER'] },
        { path: "/mrp/model-forecasts", label: "Model Forecasts", icon: TrendingUp,
          roles: ['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER'] },
        { path: "/mrp/rm-params", label: "RM Parameters", icon: Settings,
          roles: ['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER'] },
        { path: "/mrp/runs", label: "MRP Runs", icon: ClipboardList,
          roles: ['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER'] },
        { path: "/mrp/weekly-plan", label: "Weekly Order Plan", icon: Calculator,
          roles: ['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER'] },
        { path: "/mrp/draft-pos", label: "Draft POs", icon: ShoppingCart,
          roles: ['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER'] },
      ],
    },
    {
      key: "production",
      label: "Production",
      icon: Factory,
      items: [
        { path: "/cpc", label: "CPC", icon: Workflow, entity: 'ProductionPlan' },
        { path: "/cpc-dashboard", label: "CPC Dashboard", icon: Gauge, entity: 'ProductionPlan' },
        { path: "/branch-ops", label: "Branch Ops", icon: Factory, entity: 'ProductionPlan' },
        // Branch-Ops floor tool — no clean entity.
        { path: "/rm-production", label: "RM Production", icon: Factory, roles: ['BRANCH_OPS_USER'] },
        // Jul 2026 — FG Production Log mirrors RM Production Log: reads
        // `fg_production_log` (populated when a Branch Ops schedule is
        // marked Complete) with the same filters + enrichments as the
        // RM side. Gated identically to RM Production for consistency.
        { path: "/fg-production-log", label: "FG Production Log", icon: Factory, roles: ['BRANCH_OPS_USER'] },
        // REGRIND_RECYCLE_PRD §FR-B4 — Grinding Log mirrors RM Production's
        // access model: sits next to it on the Production sidebar, gated to
        // BRANCH_OPS_USER (master_admin always sees it via global override).
        { path: "/grinding-log", label: "Grinding Log", icon: Factory, roles: ['BRANCH_OPS_USER'] },
        // Job Work Lifecycle Phase 2 — Job Work Receipts (goods side).
        // Sidebar gated by ProductionBatch:CREATE — matches the write
        // authority the Create / Scrap / Return actions already require.
        // VENDOR_AGENT holds ProductionBatch:READ (read-only role) so a
        // pure READ gate would still admit them; gating on CREATE aligns
        // sidebar visibility with the "can actually work here" model and
        // excludes read-only roles cleanly. Placed under Production (not
        // Procurement) so Branch Ops finds it in its natural home.
        { path: "/jobwork-receipts", label: "Job Work Receipts", icon: PackagePlus, entity: 'ProductionBatch', action: 'CREATE' },
        // QC inspection — fallback to roles; QCChecklist/Approval is a complex composite.
        { path: "/quality", label: "Quality Control", icon: CheckCircle, roles: ['BRANCH_OPS_USER', 'QUALITY_INSPECTOR'] },
      ],
    },
    {
      key: "inventory",
      label: "Inventory",
      icon: Warehouse,
      items: [
        { path: "/raw-materials", label: "Raw Materials", icon: Package, entity: 'RawMaterial' },
        { path: "/inventory", label: "Finished Goods", icon: Warehouse, entity: 'FGInventory' },
        // Job Work Lifecycle Phase 1 — read-only "Stock at Job Worker"
        // balances page. Moved here from Procurement (Jul 2026) — Branch
        // Ops needs it and already has Inventory access; Procurement may
        // not. Route path unchanged.
        { path: "/job-work-balances", label: "Job Work Balances", icon: Layers, entity: 'Vendor' },
        // Movement tracker — read-only event-log view (Jun 2026 P0).
        // Gated on FGInventory.READ so it lights up for the same audience
        // that already sees FG/RM stock pages (TECHADMIN_VENDOR included).
        { path: "/inventory-movement", label: "Movement Tracker", icon: TrendingUp, entity: 'FGInventory' },
        // Admin-only bulk import workflows (RM + FG). Moved here May 2026 so
        // day-to-day users don't accidentally hit destructive bulk ops.
        { path: "/inventory-imports", label: "Inventory Imports", icon: Upload, entity: 'InventoryImport' },
      ],
    },
    {
      // Oct 2026 — new top-level Finance group. Money-side inward billing
      // (RM/FG Inward, Jobwork Invoices) relocated out of Procurement;
      // outward money docs (Delivery Challans, standalone Dispatch)
      // relocated out of Logistics. Nav-location only: every item keeps
      // its exact path, label, icon and entity/action gating.
      key: "finance",
      label: "Finance",
      icon: Receipt,
      items: [
        // RM Inward Entry is for users who can CREATE stock movements (i.e. record bills).
        { path: "/rm-inward", label: "RM Inward Entry", icon: PackagePlus, entity: 'RMInwardBill', action: 'CREATE' },
        // FG Inward Entry — vendor-site finished goods invoicing (Phase 4).
        { path: "/fg-inward", label: "FG Inward Entry", icon: PackagePlus, entity: 'FGInwardBill', action: 'CREATE' },
        // Job Work Lifecycle Phase 3 — Jobwork Invoices (money side, per-pc
        // conversion charges). Sidebar gated by JobworkInvoice:CREATE so the
        // link is visible only to roles that can actually book invoices
        // (Master Admin + Finance Operations). Read-only roles like
        // VENDOR_AGENT (which has JobworkInvoice:READ in the RBAC seed) are
        // hidden — matches the "money-side, Finance-only" spec.
        { path: "/jobwork-invoices", label: "Jobwork Invoices", icon: FileText, entity: 'JobworkInvoice', action: 'CREATE' },
        // DC tab — visible to everyone with Dispatch.READ. Write actions
        // (create / edit / delete) inside the page are gated by Dispatch.* via PermButton.
        { path: "/delivery-challans", label: "Delivery Challans", icon: FileText, entity: 'Dispatch' },
        { path: "/dispatch", label: "Dispatch", icon: Truck, entity: 'Dispatch' },
      ],
    },
    {
      key: "logistics",
      label: "Logistics",
      icon: Truck,
      items: [
        { path: "/logistics-bookings", label: "Bookings", icon: ClipboardList, entity: 'LogisticsBooking' },
        { path: "/ibt", label: "IBT Transfers", icon: ArrowLeftRight, entity: 'IBTTransfer' },
      ],
    },
    {
      key: "admin",
      label: "Admin",
      icon: Shield,
      items: [
        { path: "/user-management", label: "User Management", icon: Users, entity: 'User' },
        { path: "/role-management", label: "Role Permissions", icon: Shield, entity: 'Role' },
        // Reports aggregates Forecast/Dispatch/Inventory views — show to anyone with
        // READ on Forecast (covers every operational role; Finance still routes through
        // the matrix on the page itself for individual report types).
        { path: "/reports", label: "Reports", icon: FileText, entity: 'Forecast' },
        // System tools.
        // DB Explorer is MASTER_ADMIN only (Jul 2026). TECHADMIN_VENDOR
        // was previously included but that path exposes multi-DB traversal
        // with no per-collection blocklist — vendors now use the narrower
        // generic query surface at `/api/mongodb/*` (single-DB, blocklist,
        // JS-operator scan). Leaving a dead nav entry that just 403s is
        // worse UX than hiding it entirely.
        { path: "/db-explorer", label: "DB Explorer", icon: Database,
          roles: ['MASTER_ADMIN'] },
        // Database Updates and Branch Mapping are write tools — admin only.
        { path: "/database-updates", label: "Database Updates", icon: DatabaseBackup, adminOnly: true },
        { path: "/branch-mapping", label: "Branch Mapping", icon: Link2, adminOnly: true },
      ],
    },
  ];

  // Compute `show` for each item via the resolver.
  const navGroupsResolved = navGroups.map((g) => ({
    ...g,
    items: g.items.map((i) => ({ ...i, show: canSee(i) })),
  }));

  // Drop empty groups (no visible children for current role)
  const visibleGroups = navGroupsResolved
    .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
    .filter((g) => g.items.length > 0);

  // Auto-expand group containing the active route
  const activeGroupKey = useMemo(() => {
    const found = visibleGroups.find((g) => g.items.some((i) => location.pathname.startsWith(i.path)));
    return found?.key;
  }, [location.pathname, visibleGroups]);

  // Local UI state — track which groups are open (default: only the active one)
  const [openGroups, setOpenGroups] = useState({});
  const isOpen = (key) => (openGroups[key] !== undefined ? openGroups[key] : key === activeGroupKey);
  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !isOpen(key) }));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Get primary role for display
  const getPrimaryRole = () => {
    if (userRoles.length > 0) {
      return userRoles[0];
    }
    return user?.role || 'branch_user';
  };

  return (
    <div className="flex h-screen bg-zinc-50">
      <aside className="w-64 bg-zinc-900 text-white flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <h1 className="text-2xl font-black uppercase tracking-tight">Factory Ops</h1>
          <p className="text-xs text-zinc-400 mt-1 font-mono">Manufacturing Control</p>
        </div>
        
        {/* User Info */}
        <div className="p-4 border-b border-zinc-800">
          <div className="text-xs text-zinc-400 uppercase tracking-wider font-bold mb-1">Logged in as</div>
          <div className="text-sm font-mono text-white">{user?.name}</div>
          <div className="flex flex-wrap gap-1 mt-2">
            {userRoles.length > 0 ? (
              userRoles.map((role, index) => (
                <span 
                  key={index}
                  className={`text-xs font-mono px-2 py-1 rounded ${
                    role === 'MASTER_ADMIN' 
                      ? 'bg-primary text-white' 
                      : 'bg-zinc-800 text-zinc-300'
                  }`}
                >
                  {ROLE_DISPLAY_NAMES[role] || role}
                </span>
              ))
            ) : (
              <span className={`text-xs font-mono px-2 py-1 rounded ${
                user?.role === 'master_admin' 
                  ? 'bg-primary text-white' 
                  : 'bg-zinc-800 text-zinc-400'
              }`}>
                {ROLE_DISPLAY_NAMES[user?.role] || 'BRANCH USER'}
              </span>
            )}
          </div>
        </div>

        {/* Branch Selector - Hidden for CPC_PLANNER and TECH_OPS_ENGINEER (they work with universal data) */}
        {!isCPCPlanner && !hasRole('TECH_OPS_ENGINEER') && <BranchSelector />}
        
        <nav className="flex-1 p-3 overflow-y-auto" data-testid="sidebar-nav">
          {visibleGroups.map((group) => {
            const open = isOpen(group.key);
            const GroupIcon = group.icon;
            return (
              <div key={group.key} className="mb-1">
                {group.standalone ? (
                  // Top-level single-link entry — renders as one NavLink at
                  // the same visual level as a group header so it reads as
                  // a peer to "CATALOG", "DEMAND", etc.
                  <NavLink
                    to={group.items[0].path}
                    data-testid={`nav-group-${group.key}`}
                    className={({ isActive }) =>
                      `w-full flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
                        isActive
                          ? "bg-zinc-800 text-white border-l-2 border-primary -ml-[1px]"
                          : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                      }`
                    }
                  >
                    <GroupIcon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="uppercase tracking-wide text-xs font-bold">{group.label}</span>
                  </NavLink>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
                      data-testid={`nav-group-${group.key}`}
                      aria-expanded={open}
                    >
                      <span className="flex items-center gap-3">
                        <GroupIcon className="w-4 h-4" strokeWidth={1.5} />
                        <span className="uppercase tracking-wide text-xs font-bold">{group.label}</span>
                      </span>
                      {open
                        ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
                        : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
                    </button>
                    {open && (
                      <div className="mt-0.5 mb-2 ml-2 border-l border-zinc-800 pl-2">
                        {group.items.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-${item.path.slice(1)}`}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                                isActive
                                  ? "bg-zinc-800 text-white border-l-2 border-primary -ml-[1px]"
                                  : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                              }`
                            }
                          >
                            <item.icon className="w-4 h-4" strokeWidth={1.5} />
                            <span className="tracking-wide text-xs">{item.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-zinc-800">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-xs">Logout</span>
          </Button>
        </div>
        
        <div className="p-4 border-t border-zinc-800 text-xs text-zinc-500 font-mono">
          <div className="flex items-center gap-2">
            <Shield className="w-3 h-3" />
            <span>RBAC v2.0</span>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;